import * as React from 'react';

import { useQuery, QueryHookOptions, useApolloClient } from '@apollo/client';
import { chunk } from 'lodash';

import {
  GetProductsByIdBatchedQuery,
  GetProductsByIdBatchedQueryVariables,
  ProductIdType,
} from '@notino/shared/definitions/types';

import getProductsByIdBatchedQuery from '@components/Universals/BatchVPProductQuery/queries/productsByIdBatched.graphql';

type Options = {
  filterNullProducts?: boolean;
  idType?: ProductIdType;
} & Partial<
  Omit<
    QueryHookOptions<
      GetProductsByIdBatchedQuery,
      GetProductsByIdBatchedQueryVariables
    >,
    'variables'
  >
>;

export const useProductsByIdsBatched = (
  ids: (string | number)[],
  {
    filterNullProducts = true,
    skip = false,
    ssr = false,
    errorPolicy = 'all',
    onCompleted,
    idType,
    ...restOptions
  }: Options = {}
): ReturnType<
  typeof useQuery<
    GetProductsByIdBatchedQuery,
    GetProductsByIdBatchedQueryVariables
  >
> => {
  const { data, ...restResponse } = useQuery<
    GetProductsByIdBatchedQuery,
    GetProductsByIdBatchedQueryVariables
  >(getProductsByIdBatchedQuery, {
    // we have to check if ids.length === 0 to avoid call to api with empty ids
    skip: ids.length === 0 || skip,
    errorPolicy,
    ssr,
    variables: {
      ids: ids.map(String),
      idType,
    },
    onCompleted: filterNullProducts
      ? (response) => {
          onCompleted?.({
            ...response,
            vpProductByIds: response.vpProductByIds?.filter(Boolean),
          });
        }
      : onCompleted,
    ...restOptions,
  });

  const products = React.useMemo(() => {
    if (!data) {
      return data;
    }
    return {
      ...data,
      vpProductByIds: filterNullProducts
        ? data.vpProductByIds?.filter(Boolean)
        : data.vpProductByIds,
    };
  }, [filterNullProducts, data]);

  return {
    ...restResponse,
    data: products,
  };
};

type PaginatedOptions = Options & {
  pageSize: number;
};

export const useProductsByIdsPaginated = (
  ids: (string | number)[],
  { pageSize, onCompleted, ...restOptions }: PaginatedOptions
) => {
  const apollo = useApolloClient();
  const [page, setPage] = React.useState(0);

  const idBatches = chunk(ids.map(String), pageSize);
  const paginatedIds = idBatches[page] || [];

  const [fetchedItems, setFetchedItems] = React.useState<
    GetProductsByIdBatchedQuery['vpProductByIds']
  >([]);

  // Subscribe to cache updates for already fetched items
  // Syncs wishlisted state etc.
  React.useEffect(() => {
    const unsubs = idBatches.map((batch) =>
      apollo.cache.watch<
        GetProductsByIdBatchedQuery,
        GetProductsByIdBatchedQueryVariables
      >({
        query: getProductsByIdBatchedQuery,
        callback: (diff) => {
          const diffItems = diff.result?.vpProductByIds ?? [];
          setFetchedItems((prev) =>
            prev.map(
              (item) =>
                diffItems.find((diffItem) => diffItem?.id === item?.id) ?? item
            )
          );
        },
        optimistic: true,
        variables: { ids: batch, idType: restOptions.idType },
      })
    );
    return () => {
      unsubs.forEach((unsub) => unsub());
    };
  }, [ids, pageSize, restOptions.idType, apollo.cache, idBatches]);

  const { loading } = useProductsByIdsBatched(paginatedIds, {
    ...restOptions,
    onCompleted: (data) => {
      onCompleted?.(data);
      setFetchedItems((prev) => [...prev, ...data.vpProductByIds]);
    },
  });

  return {
    data: fetchedItems,
    loading: loading && fetchedItems.length === 0,
    loadMore: () => setPage((prevPage) => prevPage + 1),
  };
};
